import { Box, Divider } from '@wix/design-system';
import type { FC } from 'react';
import React, { useContext } from 'react';

import { ModalServiceContext, RicosContext } from './../../ricos-context';
import { useBiNext } from './../../_shared/bi-context-provider/bi-context-provider';
import { logInvariant } from './../../_shared/log-invariant';
import { useNodeAttrs } from './../../_shared/use-node-attrs';
import { useSpoilerPlugin } from './../../_shared/use-spoiler-plugin';
import {
  Panel,
  PanelHeader,
  ToggleButton,
  usePanelContainerElement,
} from './../../ui-components';

import { VIDEO_SETTINGS_MODAL_ID } from '../constants';
import { SETTINGS_DISABLE_DOWNLOAD_TOGGLE, SETTINGS_SPOILER_TOGGLE } from '../data-hooks';
import { VIDEO_TYPE } from '../types';

interface Props {
  nodeId: string;
}

export const SettingsModal: FC<Props> = ({ nodeId }) => {
  const { t } = useContext(RicosContext);
  const modalService = useContext(ModalServiceContext) || {};
  const isSpoilerEnabled = useSpoilerPlugin(VIDEO_TYPE);
  const containerEl = usePanelContainerElement();

  const { patchPmNodeAttrs, pmNodeAttrs } = useNodeAttrs(nodeId, 'VIDEO');
  const logBi = useBiNext({ pluginId: 'video', modalId: VIDEO_SETTINGS_MODAL_ID, nodeId });

  const closeModal = () => modalService.closeModal(VIDEO_SETTINGS_MODAL_ID);

  if (!pmNodeAttrs) {
    logInvariant(new Error('Video settings: node attrs not found'));
    return null;
  }

  const isCustomVideo = !!pmNodeAttrs.disableDownload || pmNodeAttrs.video?.src?.id;

  return (
    <Panel onCloseButtonClick={closeModal}>
      <PanelHeader title={t('VideoPlugin_Settings_Header')} />

      <Box direction="vertical" padding="SP3" gap="SP4">
        {isCustomVideo ? (
          <ToggleButton
            title={t('VideoPlugin_Settings_VideoCanBeDownloaded_Label')}
            checked={!pmNodeAttrs.disableDownload}
            size="small"
            onChange={() => {
              logBi({
                dataHook: SETTINGS_DISABLE_DOWNLOAD_TOGGLE,
                value: !!pmNodeAttrs.disableDownload,
              });
              patchPmNodeAttrs({ disableDownload: !pmNodeAttrs.disableDownload });
            }}
            infoContent={t('VideoPlugin_Settings_VideoCanBeDownloaded_Tooltip')}
            tooltipAppendTo={containerEl}
            dataHook={SETTINGS_DISABLE_DOWNLOAD_TOGGLE}
          />
        ) : null}
        {isCustomVideo && isSpoilerEnabled ? <Divider /> : null}
        {isSpoilerEnabled ? (
          <ToggleButton
            title={t('VideoSettings_Spoiler_Toggle')}
            checked={!!pmNodeAttrs.containerData?.spoiler?.enabled}
            size="small"
            onChange={() => {
              logBi({
                dataHook: SETTINGS_SPOILER_TOGGLE,
                value: !pmNodeAttrs.containerData?.spoiler?.enabled,
              });
              patchPmNodeAttrs({
                containerData: {
                  ...pmNodeAttrs.containerData,
                  spoiler: {
                    ...pmNodeAttrs.containerData?.spoiler,
                    enabled: !pmNodeAttrs.containerData?.spoiler?.enabled,
                  },
                },
              });
            }}
            infoContent={t('Spoiler_Toggle_Tooltip')}
            tooltipAppendTo={containerEl}
            dataHook={SETTINGS_SPOILER_TOGGLE}
          />
        ) : null}
      </Box>
    </Panel>
  );
};
